import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Pencil, Trash2, Loader2, Eye, FileSignature } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', company_name: '', address: '', nip: '', phone: '', email: '', website: '', custom_text: '', is_default: false };

export default function FootersPage() {
  const [footers, setFooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const fetchData = useCallback(async () => {
    try { const r = await api.get('/email-footers'); setFooters(r.data.items); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditId(null); setPreviewHtml(''); setDialogOpen(true); };
  const openEdit = (f) => { setForm({...f}); setEditId(f.id); setPreviewHtml(''); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) { await api.put(`/email-footers/${editId}`, form); toast.success('Stopka zapisana'); }
      else { await api.post('/email-footers', form); toast.success('Stopka utworzona'); }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunac szablon stopki?')) return;
    try { await api.delete(`/email-footers/${id}`); toast.success('Usunieto'); fetchData(); }
    catch { toast.error('Blad'); }
  };

  const loadPreview = async (id) => {
    try {
      const r = await api.get(`/email-footers/${id || editId}/preview`);
      setPreviewHtml(r.data.html);
    } catch {}
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-6" data-testid="footers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Stopki emaili</h1>
          <p className="text-sm text-muted-foreground mt-1">Szablony stopek z danymi firmy do kampanii mailingowych</p>
        </div>
        <Button onClick={openCreate} data-testid="footer-add-button"><Plus className="h-4 w-4 mr-2" />Nowa stopka</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {footers.map(f => (
          <Card key={f.id} className="border-border/50 bg-card/80">
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <FileSignature className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm">{f.name}</span>
                    {f.is_default && <Badge variant="outline" className="text-[10px] text-primary border-primary/30">Domyslna</Badge>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(f)}><Pencil className="h-3 w-3" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(f.id)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                {f.company_name && <p className="font-medium text-foreground">{f.company_name}</p>}
                {f.address && <p>{f.address}</p>}
                {f.nip && <p>NIP: {f.nip}</p>}
                {f.phone && <p>Tel: {f.phone}</p>}
                {f.email && <p>{f.email}</p>}
                {f.website && <p className="text-primary">{f.website}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? 'Edytuj stopke' : 'Nowa stopka emaila'}</DialogTitle>
            <DialogDescription>Dane firmy wyswietlane na dole kazdego emaila kampanii</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2"><Label>Nazwa szablonu</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="np. Stopka Tejwaz" /></div>
              <Separator />
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Dane firmy</p>
              <div className="space-y-2"><Label>Nazwa firmy / nadawcy</Label><Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} placeholder="Tejwaz.pl Sp. z o.o." /></div>
              <div className="space-y-2"><Label>Adres siedziby</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="ul. Przykladowa 1, 00-001 Warszawa" /></div>
              <div className="space-y-2"><Label>NIP</Label><Input value={form.nip} onChange={e => setForm({...form, nip: e.target.value})} placeholder="123-456-78-90" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Telefon</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+48 500 600 700" /></div>
                <div className="space-y-2"><Label>Email kontaktowy</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="kontakt@firma.pl" /></div>
              </div>
              <div className="space-y-2"><Label>Strona WWW</Label><Input value={form.website} onChange={e => setForm({...form, website: e.target.value})} placeholder="https://tejwaz.pl" /></div>
              <div className="space-y-2"><Label>Dodatkowy tekst (opcjonalny)</Label><Textarea value={form.custom_text} onChange={e => setForm({...form, custom_text: e.target.value})} rows={2} placeholder="np. Regulamin: https://... | Polityka prywatnosci: https://..." /></div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_default} onCheckedChange={v => setForm({...form, is_default: v})} />
                <Label className="text-xs">Domyslna stopka (uzywana gdy nie wybrano innej)</Label>
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">Podglad stopki</p>
              <div className="border border-border/50 rounded-lg p-4 bg-white text-black">
                <div style={{borderTop:'1px solid #ddd', paddingTop:12, marginTop:8}}>
                  <p style={{fontSize:11, color:'#666', lineHeight:'1.6'}}>
                    {[form.company_name, form.address, form.nip && `NIP: ${form.nip}`].filter(Boolean).join(' | ') || 'Nazwa firmy | Adres'}
                  </p>
                  <p style={{fontSize:11, color:'#888', lineHeight:'1.6'}}>
                    {[form.phone && `Tel: ${form.phone}`, form.email && `Email: ${form.email}`, form.website].filter(Boolean).join(' | ') || 'Telefon | Email | WWW'}
                  </p>
                  {form.custom_text && <p style={{fontSize:11, color:'#888', lineHeight:'1.6'}}>{form.custom_text}</p>}
                  <p style={{fontSize:11, color:'#999', borderTop:'1px solid #eee', paddingTop:8, marginTop:8}}>
                    Nie chcesz otrzymywac wiadomosci? <span style={{color:'#2db8b0', textDecoration:'underline'}}>Kliknij tutaj aby sie wypisac</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleSave} disabled={saving || !form.name || !form.company_name} data-testid="footer-form-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editId ? 'Zapisz' : 'Dodaj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
